<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import logoUrl from '@/assets/login-logo.png';
import AuthLayout from "@/layout/auth/AuthLayout.vue";
import { Head, Link, useForm } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';

const props = defineProps({
    email: String,
    token: String,
});

const form = useForm({
    token: props.token,
    email: props.email,
    password: '',
    password_confirmation: '',
});

const { showLoading, hideLoading } = useLoading();

// Password confirmation validation
const passwordError = ref('');

watch([() => form.password, () => form.password_confirmation], ([password, confirmation]) => {
    if (confirmation && password !== confirmation) {
        passwordError.value = 'Passwords do not match';
    } else {
        passwordError.value = '';
    }
});

const submit = () => {
    // Check password confirmation before submitting
    if (form.password !== form.password_confirmation) {
        passwordError.value = 'Passwords do not match';
        return;
    }

    showLoading($t('Updating password...'));
    form.post(route('client.password.update'), {
        onFinish: () => {
            form.reset('password', 'password_confirmation');
            hideLoading();
        },
    });
};

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const supportMenu = ref();
const supportItems = computed(() => ([
    {
        label: $t('Whatsapp'),
        icon: 'pi pi-whatsapp',
        url: 'https://wa.me/5547989104747?text=Ol%C3%A1%21+Preciso+de+ajuda+para+logar+no+sistema',
        target: '_blank'
    },
    {
        label: $t('Telegram'),
        icon: 'pi pi-telegram',
        url: 'https://t.me/maxpiress',
        target: '_blank'
    },
    {
        label: $t('Skype'),
        icon: 'pi pi-skype',
        url: 'https://msng.link/o/?maxpires=sk',
        target: '_blank'
    }
]));

defineOptions({
    layout: AuthLayout,
});
</script>

<template>
    <Head :title="$t('Reset Password')" />

    <div class="border mx-2 sm:mx-auto px-5 sm:px-20 pb-16 rounded-2xl">
        <h1 class="text-4xl sm:text-5xl font-medium text-center mx-auto leading-[125%] my-12 sm:min-w-96">
            <img :src="logoUrl" class="h-16 w-auto mx-auto" alt="Logo"/>
        </h1>

        <form @submit.prevent="submit" class="max-w-[35rem] mx-auto space-y-4">
            <div class="mb-4 flex flex-col gap-2">
                <InputText
                    v-model="form.email"
                    :placeholder="$t('Email')"
                    type="email"
                    :class="{ 'p-invalid': form.errors.email }"
                    required
                    readonly
                />
                <small v-if="form.errors.email" class="p-error">{{ form.errors.email }}</small>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <Password
                        v-model="form.password"
                        :placeholder="$t('New Password')"
                        :feedback="false"
                        :class="{ 'p-invalid': form.errors.password }"
                        class="w-full"
                        required
                        autocomplete="new-password"
                    />
                    <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
                </div>
                <div class="flex flex-col gap-2">
                    <Password
                        v-model="form.password_confirmation"
                        :placeholder="$t('Confirm Password')"
                        :feedback="false"
                        :class="{ 'p-invalid': form.errors.password_confirmation || passwordError }"
                        class="w-full"
                        required
                        autocomplete="new-password"
                    />
                    <small v-if="form.errors.password_confirmation" class="p-error">{{ form.errors.password_confirmation }}</small>
                    <small v-if="passwordError && !form.errors.password_confirmation" class="p-error">{{ passwordError }}</small>
                </div>
            </div>

            <div class="space-y-8 mt-8 text-sm">
                <div class="flex items-center justify-between">
                    <div class="text-surface-500 text-center leading-6">
                        {{ $t('Remember your password?') }}
                        <Link
                            :href="route('client.login')"
                            class="text-primary-active font-medium hover:opacity-75 transition-all"
                        >
                            {{ $t('Back to login') }}
                        </Link>
                    </div>
                </div>

                <button
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    type="submit"
                    class="landing-button-primary w-full"
                >
                    {{ $t('Reset Password') }}
                </button>
            </div>
        </form>

    </div>
</template>
